import { LoadingStateOps } from "../../../domain/data-layer/loading-state";
import { useLoadingSetState } from "../../../hooks";
import { useEffect } from "react";
import { useMountedRef } from "../../../hooks/use-mounted";
import { EasySizeConfiguration, EasySizeInstance, WindowEasySizeT } from "./types";
import { useEasySizeScript } from "./use-easy-size-script";

declare global {
  interface Window {
    EasySize?: WindowEasySizeT;
  }
}

export function useEasySizeInstance(
  configuration: Omit<EasySizeConfiguration, "loaded">,
  script: {
    src: string;
    integrity: string | undefined;
  },
  idKey: number,
): { instance: EasySizeInstance } | null {
  const [state, setLoaded, setFailed, setState] = useLoadingSetState<{
    instance: EasySizeInstance;
    isStarted: boolean;
  }>(LoadingStateOps.loadingS);

  const isMounted = useMountedRef();
  const windowES = useEasySizeScript(script);

  const onEasySizeStarted: EasySizeConfiguration["loaded"] = a => {
    if (!isMounted.current) {
      return;
    }

    setState(s => {
      if (s.__tag === "loaded") {
        return {
          __tag: "loaded",
          data: {
            ...s.data,
            isStarted: true,
          },
        };
      } else {
        console.error(`easy size failed to start`);

        return LoadingStateOps.asFailed(`easy size failed to start`);
      }
    });
  };

  function getFullConfiguration(): EasySizeConfiguration {
    return {
      ...configuration,
      loaded: onEasySizeStarted,
    };
  }

  useEffect(() => {
    if (windowES.__tag === "loaded") {
      const { current } = windowES.data.ref;

      if (!current) {
        return;
      }

      const easySize = new current(getFullConfiguration());

      easySize.start();

      setLoaded({ instance: easySize, isStarted: false });
    }
  }, [windowES, idKey]);

  if (state.__tag === "loaded" && state.data.isStarted) {
    return {
      instance: state.data.instance,
    };
  } else {
    return null;
  }
}
